import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Layout } from '@/components/layout'
import {
  useUsers,
  useUpdateUser,
  useUpdateUserRole,
  useCreateUser,
  useChangeUserPassword,
  useDeleteUser,
  useActivateUser,
  useDeactivateUser,
} from '@/api/users'
import { Users, Search, UserPlus, RefreshCw } from 'lucide-react'
import { useToast } from '@/lib/hooks/useToast'
import { CreateUserDialog } from './components/CreateUserDialog'
import { ViewUserDialog } from './components/ViewUserDialog'
import { ChangePasswordDialog } from './components/ChangePasswordDialog'
import { EditUserDialog } from '@/features/admin/components/EditUserDialog'
import { ChangeRoleDialog } from '@/features/admin/components/ChangeRoleDialog'
import { DeleteUserDialog } from '@/features/admin/components/DeleteUserDialog'
import { UserCard } from '@/features/admin/components/UserCard'
import type { User } from '@/lib/types'
import { useAuth } from '@/lib/hooks/useAuth'
import { useHasPermission } from '@/lib/hooks/usePermission'

export function AdminUsersPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { user: currentUser } = useAuth()

  // Permission checks
  const canListUsers = useHasPermission('users.list')
  const canCreateUsers = useHasPermission('users.create')
  const canViewUsers = useHasPermission('users.view')

  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<User | null>(null)
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)

  const perPage = 20

  const { data: usersData, isLoading, refetch } = useUsers(currentPage, perPage)
  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser()
  const { mutateAsync: updateUserRole, isPending: isUpdatingRole } = useUpdateUserRole()
  const { mutateAsync: createUser, isPending: isCreating } = useCreateUser()
  const { mutateAsync: deleteUser, isPending: isDeleting } = useDeleteUser()
  const { mutateAsync: changeUserPassword, isPending: isChangingPassword } = useChangeUserPassword()
  const { mutateAsync: activateUser } = useActivateUser()
  const { mutateAsync: deactivateUser } = useDeactivateUser()

  // Form state
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    language: '',
    timezone: '',
  })

  const [roleForm, setRoleForm] = useState({
    roleId: null as string | null,
  })

  const [createForm, setCreateForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    display_name: '',
    password: '',
    role: 'user',
    language: 'nl',
    timezone: 'Europe/Amsterdam',
  })

  const [passwordForm, setPasswordForm] = useState({
    password: '',
  })

  // Filter users by search query and filters
  const filteredUsers = usersData?.users.filter((user: User) => {
    const query = searchQuery.toLowerCase()
    const matchesSearch =
      user.email.toLowerCase().includes(query) ||
      user.firstName.toLowerCase().includes(query) ||
      user.lastName.toLowerCase().includes(query) ||
      (user.displayName && user.displayName.toLowerCase().includes(query))

    // Check RBAC roles for filtering
    const matchesRole =
      roleFilter === 'all' ||
      (user.roles && user.roles.some(r => r.slug === roleFilter))

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user.isActive) ||
      (statusFilter === 'inactive' && !user.isActive)

    return matchesSearch && matchesRole && matchesStatus
  })

  const handleClearFilters = () => {
    setSearchQuery('')
    setRoleFilter('all')
    setStatusFilter('all')
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800'
      case 'admin':
        return 'bg-red-100 text-red-800'
      case 'user':
        return 'bg-blue-100 text-blue-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleEditClick = (user: User) => {
    setSelectedUser(user)
    setEditForm({
      first_name: user.firstName,
      last_name: user.lastName,
      display_name: user.displayName || '',
      language: user.language,
      timezone: user.timezone,
    })
    setIsEditDialogOpen(true)
  }

  const handleRoleClick = (user: User) => {
    setSelectedUser(user)
    // Get the first role ID if user has roles, otherwise null
    const firstRoleId = user.roles && user.roles.length > 0 ? user.roles[0].id : null
    setRoleForm({ roleId: firstRoleId })
    setIsRoleDialogOpen(true)
  }

  const handleViewClick = (user: User) => {
    setSelectedUser(user)
    setIsViewDialogOpen(true)
  }

  const handleEditSubmit = async () => {
    if (!selectedUser) return

    try {
      await updateUser({
        userId: selectedUser.id,
        data: editForm,
      })
      setIsEditDialogOpen(false)
      refetch()
    } catch (error) {
      toast({
        title: t('admin.users.updateError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleRoleSubmit = async () => {
    if (!selectedUser || !roleForm.roleId) return

    try {
      await updateUserRole({
        userId: selectedUser.id,
        roleIds: [roleForm.roleId], // RBAC API expects array of role IDs
      })
      setIsRoleDialogOpen(false)
      refetch()
      toast({
        title: t('admin.users.roleUpdated'),
        description: t('admin.users.roleUpdateSuccess'),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('admin.users.roleUpdateError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleToggleActive = async (user: User) => {
    // Note: This function is kept for local users
    // Azure users are managed entirely via Entra ID and should not reach here
    // as the UI doesn't show toggle active/inactive for Azure users
    try {
      if (user.isActive) {
        await deactivateUser(user.id)
        toast({
          title: t('admin.users.statusUpdated'),
          description: t('admin.users.userDeactivated'),
          variant: 'success',
        })
      } else {
        await activateUser(user.id)
        toast({
          title: t('admin.users.statusUpdated'),
          description: t('admin.users.userActivated'),
          variant: 'success',
        })
      }
      refetch()
    } catch (error) {
      toast({
        title: t('admin.users.toggleError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleCreateSubmit = async () => {
    try {
      await createUser(createForm)
      setIsCreateDialogOpen(false)
      setCreateForm({
        email: '',
        first_name: '',
        last_name: '',
        display_name: '',
        password: '',
        role: 'user',
        language: 'nl',
        timezone: 'Europe/Amsterdam',
      })
      refetch()
    } catch (error) {
      toast({
        title: t('admin.users.createError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleChangePasswordClick = (user: User) => {
    setSelectedUserForPassword(user)
    setPasswordForm({ password: '' })
    setIsPasswordDialogOpen(true)
  }

  const handleChangePasswordSubmit = async () => {
    if (!selectedUserForPassword || !passwordForm.password) return

    try {
      await changeUserPassword({
        userId: selectedUserForPassword.id,
        newPassword: passwordForm.password,
      })
      setIsPasswordDialogOpen(false)
      setSelectedUserForPassword(null)
      setPasswordForm({ password: '' })
      toast({
        title: t('admin.users.passwordChangeSuccess'),
        description: t('admin.users.passwordChanged'),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('admin.users.passwordChangeError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const handleDeleteClick = (user: User) => {
    setSelectedUserForDelete(user)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteSubmit = async () => {
    if (!selectedUserForDelete) return

    try {
      await deleteUser(selectedUserForDelete.id)
      setIsDeleteDialogOpen(false)
      setSelectedUserForDelete(null)
      refetch()
      toast({
        title: t('admin.users.userDeleted'),
        description: t('admin.users.userDeletedDescription'),
        variant: 'success',
      })
    } catch (error) {
      toast({
        title: t('admin.users.deleteError'),
        description: error instanceof Error ? error.message : t('admin.users.unknownError'),
        variant: 'destructive',
      })
    }
  }

  const totalPages = usersData ? Math.ceil(usersData.total / perPage) : 1

  // Require at least list or view permission to access this page
  if (!canListUsers && !canViewUsers) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <p className="text-muted-foreground">
                  {t(
                    'auth.accessDenied.message',
                    'You do not have permission to access this page.'
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex-column md:flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                {t('admin.users.title')}
              </h1>
              <p className="text-muted-foreground mt-2">{t('admin.users.description')}</p>
            </div>
            <div className="flex space-x-2 mt-4 md:mt-0">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('admin.vernieuwen')}
              </Button>
              {canCreateUsers && (
                <Button onClick={() => setIsCreateDialogOpen(true)} size="sm">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('admin.users.createUser')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('admin.users.filters')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('admin.users.search')}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('admin.users.searchPlaceholder')}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">{t('admin.users.role')}</label>
                  <Select value={roleFilter} onValueChange={setRoleFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin.users.selectRole')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('admin.users.allRoles')}</SelectItem>
                      <SelectItem value="super_admin">{t('admin.users.roleSuperAdmin')}</SelectItem>
                      <SelectItem value="admin">{t('admin.users.roleAdmin')}</SelectItem>
                      <SelectItem value="user">{t('admin.users.roleUser')}</SelectItem>
                      <SelectItem value="viewer">{t('admin.users.roleViewer')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    {t('admin.users.status')}
                  </label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('admin.users.selectStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('admin.users.allStatuses')}</SelectItem>
                      <SelectItem value="active">{t('admin.users.active')}</SelectItem>
                      <SelectItem value="inactive">{t('admin.users.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Clear Filters */}
                <Button variant="outline" size="sm" onClick={handleClearFilters} className="w-full">
                  {t('admin.users.clearFilters')}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Users Table */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5" />
                    <span>{t('admin.users.userList')}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {filteredUsers?.length || 0} {t('admin.users.of')} {usersData?.total || 0}{' '}
                    {t('admin.users.users')}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                    {t('admin.users.loading')}
                  </div>
                ) : filteredUsers && filteredUsers.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Users className="h-6 w-6 text-muted-foreground mr-2" />
                    {t('admin.users.noUsers')}
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {filteredUsers?.map((user: User) => (
                        <UserCard
                          key={user.id}
                          user={user}
                          currentUser={currentUser}
                          getRoleBadgeColor={getRoleBadgeColor}
                          onViewClick={handleViewClick}
                          onEditClick={handleEditClick}
                          onRoleClick={handleRoleClick}
                          onChangePasswordClick={handleChangePasswordClick}
                          onToggleActive={handleToggleActive}
                          onDeleteClick={handleDeleteClick}
                          isChangingPassword={isChangingPassword}
                          isDeleting={isDeleting}
                        />
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 pt-6 border-t">
                        <p className="text-sm text-muted-foreground">
                          {t('admin.users.showing')} {(currentPage - 1) * perPage + 1} -{' '}
                          {Math.min(currentPage * perPage, usersData?.total || 0)}{' '}
                          {t('admin.users.of')} {usersData?.total || 0}
                        </p>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            {t('admin.users.previous')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            {t('admin.users.next')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit User Dialog */}
      <EditUserDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        form={editForm}
        onFormChange={setEditForm}
        onSubmit={handleEditSubmit}
        isLoading={isUpdating}
      />

      {/* Change Role Dialog */}
      <ChangeRoleDialog
        open={isRoleDialogOpen}
        onOpenChange={setIsRoleDialogOpen}
        roleId={roleForm.roleId}
        onRoleChange={roleId => setRoleForm({ roleId })}
        onSubmit={handleRoleSubmit}
        isLoading={isUpdatingRole}
      />

      {/* Create User Dialog */}
      <CreateUserDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        form={createForm}
        onFormChange={setCreateForm}
        onSubmit={handleCreateSubmit}
        isLoading={isCreating}
      />

      {/* View User Dialog */}
      <ViewUserDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        user={selectedUser}
        getRoleBadgeColor={getRoleBadgeColor}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
        user={selectedUserForPassword}
        password={passwordForm.password}
        onPasswordChange={password => setPasswordForm({ password })}
        onSubmit={handleChangePasswordSubmit}
        isLoading={isChangingPassword}
        getRoleBadgeColor={getRoleBadgeColor}
      />

      {/* Delete User Dialog */}
      <DeleteUserDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        user={selectedUserForDelete}
        onConfirm={handleDeleteSubmit}
        isDeleting={isDeleting}
      />
    </Layout>
  )
}
